"use client";
import { Button } from "@gigpro/ui/Button";
import { Close, Upload } from "@gigpro/ui/Icons";
import React from "react";
import { uniqueBy } from "remeda";

interface InputFileProps extends Omit<React.ComponentProps<"div">, "onChange"> {
  value?: Array<File>;
  error?: Array<{ error: string; file: File }>;
  loading?: boolean;
  accept: string;
  succeedFiles?: Array<File>;
  onChange: (files: Array<File>) => void;

  label?: string;
}

export const InputFile = ({
  onChange,
  loading,
  value = [],
  error,
  accept,
  succeedFiles,
  label = "Attach a file",
  ...rest
}: Readonly<InputFileProps>) => {
  const ref = React.useRef<HTMLInputElement>(null);
  const onRemove = (idx: number) => onChange(value?.filter((_, i) => i !== idx));

  return (
    <div {...rest}>
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2">
        <Button
          variant="contained"
          size="small"
          className="shrink-0"
          onClick={() => ref?.current?.click()}
          disabled={loading}
        >
          {label}
          <Upload className="mr-1 size-3.5" />
        </Button>
        <input
          className="hidden"
          ref={ref}
          multiple
          type="file"
          value=""
          accept={accept}
          onChange={
            (e) => onChange(uniqueBy([...(value ?? []), ...Array.from(e.target.files || [])], (file) => file.name)) // eslint-disable-line e18e/prefer-spread-syntax -- FileList is not spreadable
          }
        />
        {accept && <p className="tracking-tight text-gray">{accept} are supported</p>}
      </span>
      {value.map((file, idx) => {
        const fileError = error?.find((e) => e.file.name === file.name)?.error;
        const isSuccess = !!succeedFiles?.find((f) => f.name === file.name);
        return (
          <div
            key={file.name}
            className="flex items-center gap-1 font-semibold text-brand data-[error=true]:text-error data-[succeed=true]:text-success"
            data-loading={loading}
            data-error={!!fileError}
            data-succeed={isSuccess}
          >
            <span className="text-small font-semibold underline">{file.name}</span>
            <Button
              variant="text"
              icon
              data-error={!!fileError}
              data-success={!loading && isSuccess}
              className="disabled:bg-transparent data-[error=true]:text-error data-[success=true]:text-success"
              loading={!isSuccess && loading}
              disabled={loading}
              onClick={() => onRemove(idx)}
            >
              <Close />
            </Button>
            {!!fileError && <p className="text-error">({fileError})</p>}
          </div>
        );
      })}
    </div>
  );
};

InputFile.displayName = "InputFile";
